import * as THREE from 'three';
import { cloth, emis, leather, std } from '../materials.ts';
import { G, M, V, glowSprite, ring } from '../prims.ts';
import { figure } from './heroes.ts';
import { begin, done, type BaseOpts, type Model } from './types.ts';

/*
 * The cleric's last road, chapter 4 (docs/sendas/clerigo.md, «La Ermita»): its people and its enemy, painted miniatures.
 *
 * - Madre Aud (`aud`), as she is played in the memory: the prioress of the Hermitage, old and straight, a deep hood of
 *   slate wool over a white coif, the Order's grey stole crossed on her breast, a rope belt, and in her hand the stub of
 *   a wax candle in a little iron cup (its flame a cold white glow).
 * - Tessa at twenty-two (`tessaYoung`): a young woman of the terraces, dark hair in a long braid, a grey wool dress, a
 *   slate shawl, the Order's novice cord at her waist.
 * - Her mother (`tessaMother`): lying on her back under a pale shroud, her hands folded, a pillow of folded wool; the
 *   altar is the place's own stone.
 * - The Doliente (`doliente`): tall and thin, taller than a man by a head; no hands; a veil of black ink from the crown of
 *   her head down her back and out along the floor for metres behind her, rippling; a sick violet sheen in its folds and
 *   two pale lines down where a face would be (she weeps: she doesn't speak).
 * - A tongue of ink (`lengua`): the end of the veil crawling along the floor toward a lamp: a flat black ribbon with a
 *   curled tip of hooks, the violet sheen under it.
 *
 * Colors: cold greys and slate, the coif and the shroud a cold pale grey (never cream: under the lamps' warm light it
 * turned pink), the candles' light white; the ink black with violet. Never gold.
 */

const SKIN = '#cfa58c';
const SKIN_OLD = '#c49c8c';
const SLATE = '#3f4652';
const PALE = '#c9ced6';
const INK = '#0b0912';
const INK_SHEEN = '#3a2a5a';

const face = (h: THREE.Object3D, y: number, z: number, s = 1, tint = '#b98c74'): void => {
  for (const k of [-1, 1]) M(new THREE.SphereGeometry(0.012 * s, 5, 4), std('#141214'), h).position.set(k * 0.042 * s, y, z);
  const n = M(new THREE.ConeGeometry(0.016 * s, 0.04 * s, 5), std(tint, { roughness: 0.85 }), h);
  n.position.set(0, y - 0.028 * s, z + 0.016 * s);
  n.rotation.x = Math.PI / 2 + 0.2;
};

/** Madre Aud, the prioress: the hood, the coif, the stole, the candle. */
const aud = (o?: BaseOpts): Model => {
  const c = begin(o, 0.6, 901);
  const robe = cloth('#4a5059');
  const hood = cloth(SLATE);
  const skin = std(SKIN_OLD, { roughness: 0.9 });
  const f = figure({ legs: robe, boots: leather('#1e1a17'), torso: robe, shoulders: hood, arms: robe, forearms: robe, hands: skin, skin, cape: SLATE, capeLen: 1.15, capeR: 0.34 });
  c.g.add(f.fig);
  f.fig.scale.set(0.9, 0.98, 0.9);
  const b = f.bodyAbs;
  const h = f.headAbs;
  // The robe long to the ground.
  const skirt = M(new THREE.CylinderGeometry(0.2, 0.3, 0.8, 12), robe, b);
  skirt.position.y = 0.6;
  // The Order's grey stole crossed on her breast, its ends hanging.
  const stole = cloth('#9aa0a8');
  for (const k of [-1, 1]) {
    const band = M(new THREE.BoxGeometry(0.06, 0.62, 0.02), stole, b);
    band.position.set(k * 0.07, 1.18, 0.2);
    band.rotation.z = k * 0.32;
  }
  // The rope belt.
  ring(b, 0.18, 0.018, std('#6a6258', { roughness: 1 }), 1.0, 5, 14);
  // The coif, a cold pale grey round the face; the deep hood over it.
  M(new THREE.SphereGeometry(0.128, 12, 9, 0, Math.PI * 2, 0, Math.PI * 0.62), cloth(PALE), h).position.set(0, 1.645, -0.01);
  const hd = M(new THREE.SphereGeometry(0.165, 12, 9, 0, Math.PI * 2, 0, Math.PI * 0.68), hood, h);
  hd.position.set(0, 1.64, -0.035);
  hd.scale.set(1, 1.08, 1.12);
  face(h, 1.648, 0.112, 1, '#b08878');
  // The candle stub in its iron cup, in her right hand.
  const cup = G(f.rhAbs, 0.29, 0.86, 0.2);
  M(new THREE.CylinderGeometry(0.05, 0.035, 0.04, 8), std('#2a2d32', { roughness: 0.5, metalness: 0.4 }), cup);
  M(new THREE.CylinderGeometry(0.025, 0.025, 0.08, 8), std('#d9dde4', { roughness: 0.7 }), cup).position.y = 0.05;
  M(new THREE.SphereGeometry(0.014, 6, 5), emis('#ffffff', '#f4f8ff', 2.4), cup).position.y = 0.11;
  glowSprite(cup, '#eef4ff', 0.55, V(0, 0.12, 0), 0.8);
  f.rig.armR.rotation.x = -0.75;
  return done(c, 1.86, () => undefined, f.rig);
};

/** Tessa at twenty-two: the braid, the grey dress, the slate shawl, the novice's cord. */
const tessaYoung = (o?: BaseOpts): Model => {
  const c = begin(o, 0.56, 902);
  const dress = cloth('#5a5f68');
  const shawl = cloth(SLATE);
  const skin = std(SKIN, { roughness: 0.85 });
  const f = figure({ legs: dress, boots: leather('#2a221c'), torso: dress, shoulders: shawl, arms: dress, forearms: dress, hands: skin, skin });
  c.g.add(f.fig);
  f.fig.scale.set(0.86, 0.94, 0.86);
  const b = f.bodyAbs;
  const h = f.headAbs;
  const skirt = M(new THREE.CylinderGeometry(0.19, 0.29, 0.66, 12), dress, b);
  skirt.position.y = 0.66;
  ring(b, 0.21, 0.06, shawl, 1.4, 6, 14);
  ring(b, 0.17, 0.014, cloth('#d0d4da'), 1.0, 5, 14);
  // Dark hair, parted, a long braid down her back.
  const hair = std('#2e2420', { roughness: 1 });
  M(new THREE.SphereGeometry(0.128, 12, 9, 0, Math.PI * 2, 0, Math.PI * 0.56), hair, h).position.set(0, 1.655, -0.012);
  for (let i = 0; i < 5; i++) M(new THREE.SphereGeometry(0.04 - i * 0.004, 7, 5), hair, h).position.set(0, 1.56 - i * 0.075, -0.13 - i * 0.012);
  face(h, 1.652, 0.114);
  return done(c, 1.72, () => undefined, f.rig);
};

/** Tessa's mother, lying under her shroud (the altar's stone is the place's). */
const tessaMother = (o?: BaseOpts): Model => {
  const c = begin(o, 0.5, 903);
  // (Up on the altar's stone: she lies on it in the memory and its dawn.)
  const g = G(c.g, 0, 0.9, 0);
  const shroud = cloth('#b4bac3');
  const body = M(new THREE.CapsuleGeometry(0.2, 0.95, 4, 10), shroud, g);
  body.rotation.x = Math.PI / 2;
  body.scale.set(1.1, 1, 0.55);
  body.position.set(0, 0.12, -0.12);
  // Her folded hands, the shroud's fold across.
  M(new THREE.BoxGeometry(0.42, 0.025, 0.08), cloth('#9da3ab'), g).position.set(0, 0.23, 0.08);
  for (const k of [-1, 1]) M(new THREE.SphereGeometry(0.04, 7, 5), std(SKIN_OLD, { roughness: 0.9 }), g).position.set(k * 0.04, 0.25, 0.12);
  M(new THREE.BoxGeometry(0.36, 0.08, 0.2), cloth('#8d939b'), g).position.set(0, 0.1, 0.56);
  const head = G(g, 0, 0.2, 0.52);
  head.rotation.x = -1.2;
  M(new THREE.SphereGeometry(0.1, 12, 10), std(SKIN_OLD, { roughness: 0.9 }), head);
  M(new THREE.SphereGeometry(0.106, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.5), std('#d6d9de', { roughness: 1 }), head).position.y = 0.01;
  for (const k of [-1, 1]) M(new THREE.BoxGeometry(0.03, 0.007, 0.01), std('#4b3a34'), head).position.set(k * 0.036, 0.0, 0.094);
  return done(c, 0.5);
};

/** The Doliente: tall and thin, no hands, the veil of ink trailing metres behind her along the floor. */
const doliente = (o?: BaseOpts): Model => {
  const c = begin(o, 0.7, 904);
  const ink = std(INK, { roughness: 0.6, emissive: INK_SHEEN, emissiveIntensity: 0.35 });
  const sheen = std('#2a1e44', { roughness: 0.4, emissive: '#6a4aa8', emissiveIntensity: 0.6 });
  const g = G(c.g, 0, 0, 0);
  // The body: a long narrow column of veil from the floor to the shoulders, wider at the hem.
  const robe = M(new THREE.CylinderGeometry(0.16, 0.42, 1.9, 12, 3, true), ink, g);
  robe.position.y = 0.95;
  // Shoulders, sloped; the head under the veil, a little bowed.
  const sh = M(new THREE.SphereGeometry(0.22, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.5), ink, g);
  sh.position.y = 1.86;
  sh.scale.set(1.3, 0.55, 0.9);
  const head = G(g, 0, 2.06, 0.02);
  head.rotation.x = 0.28;
  const hood = M(new THREE.SphereGeometry(0.17, 12, 10), ink, head);
  hood.scale.set(0.9, 1.25, 1);
  // Where a face would be: dark, two pale lines down it.
  M(new THREE.SphereGeometry(0.11, 10, 8, -Math.PI / 2, Math.PI, Math.PI * 0.25, Math.PI * 0.5), std('#050408', { roughness: 1 }), head).position.set(0, -0.01, 0.06);
  for (const k of [-1, 1]) {
    const tear = M(new THREE.BoxGeometry(0.01, 0.16, 0.01), emis('#cfd8ee', '#9fb0d8', 1.2), head);
    tear.position.set(k * 0.035, -0.06, 0.155);
  }
  // The veil: from the crown down her back and out along the floor in a long ribbon of panels, each a little lower.
  const panels: THREE.Mesh[] = [];
  const cape = M(new THREE.PlaneGeometry(0.62, 1.6, 1, 4), ink, g);
  cape.position.set(0, 1.25, -0.2);
  cape.rotation.x = 0.12;
  (cape.material as THREE.Material).side = THREE.DoubleSide;
  for (let i = 0; i < 9; i++) {
    const w = 0.9 - i * 0.06;
    const p = M(new THREE.PlaneGeometry(w, 0.55, 1, 1), i % 3 === 1 ? sheen : ink, g);
    (p.material as THREE.Material).side = THREE.DoubleSide;
    p.rotation.x = -Math.PI / 2;
    p.position.set(0, 0.03 + i * 0.002, -0.45 - i * 0.5);
    panels.push(p);
  }
  glowSprite(g, '#6a4aa8', 2.2, V(0, 0.4, -1.4), 0.25);
  const tick = (n: number): void => {
    // The veil ripples along the floor, its hem breathing.
    panels.forEach((p, i) => {
      p.position.x = Math.sin(n * 1.4 - i * 0.7) * (0.06 + i * 0.03);
      p.rotation.z = Math.sin(n * 1.1 - i * 0.6) * 0.12;
    });
    cape.rotation.x = 0.12 + Math.sin(n * 0.9) * 0.05;
    robe.scale.x = robe.scale.z = 1 + Math.sin(n * 1.7) * 0.03;
    head.rotation.z = Math.sin(n * 0.6) * 0.05;
  };
  return done(c, 2.3, tick);
};

/** A tongue of ink: the veil's end crawling on the floor, a flat ribbon and a curled tip of hooks. */
const lengua = (o?: BaseOpts): Model => {
  const c = begin(o, 0.45, 905);
  const ink = std(INK, { roughness: 0.55, emissive: INK_SHEEN, emissiveIntensity: 0.45 });
  const g = G(c.g, 0, 0, 0);
  const segs: THREE.Mesh[] = [];
  for (let i = 0; i < 5; i++) {
    const s = M(new THREE.CylinderGeometry(0.16 - i * 0.02, 0.18 - i * 0.02, 0.06, 9), ink, g);
    s.position.set(0, 0.04, -i * 0.22);
    s.scale.set(1, 1, 1.4);
    segs.push(s);
  }
  // The tip, curled up, its hooks.
  const tip = G(g, 0, 0.06, 0.2);
  for (let k = -2; k <= 2; k++) {
    const hook = M(new THREE.ConeGeometry(0.025, 0.16, 5), ink, tip);
    hook.position.set(k * 0.05, 0.05, 0.04);
    hook.rotation.x = -0.9 + Math.abs(k) * 0.15;
  }
  glowSprite(g, '#6a4aa8', 0.9, V(0, 0.05, -0.3), 0.4);
  const tick = (n: number): void => {
    segs.forEach((s, i) => {
      s.position.x = Math.sin(n * 4 - i * 0.9) * 0.05;
    });
    tip.rotation.x = Math.sin(n * 5) * 0.2;
  };
  return done(c, 0.3, tick);
};

/** The cleric's chapter 4 people by name (null: not one of them). */
export const ermitaModel = (id: string, o?: BaseOpts): Model | null => {
  switch (id) {
    case 'aud':
      return aud(o);
    case 'tessaYoung':
      return tessaYoung(o);
    case 'tessaMother':
      return tessaMother(o);
    default:
      return null;
  }
};

/** Its nightmares' own looks (the Doliente is a wraith in the sim, her tongues draggers). */
export { doliente, lengua };
